import React, { useState, useEffect } from 'react';
import { Alert, Button, Space } from 'antd';
import { ExclamationCircleOutlined, ReloadOutlined } from '@ant-design/icons';
import { TokenStorage } from '../utils/token-storage';
import { useAuth } from '../hooks/useAuth';

interface TokenExpirationWarningProps {
  onRefresh?: () => void;
}

export const TokenExpirationWarning: React.FC<TokenExpirationWarningProps> = ({ onRefresh }) => {
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState<number>(0);
  const { refreshToken } = useAuth();

  useEffect(() => {
    const checkExpiration = () => {
      if (TokenStorage.willExpireSoon(5)) { // Show warning 5 minutes before expiration
        const timeUntilExpiration = TokenStorage.getTimeUntilExpiration();
        if (timeUntilExpiration !== null) {
          setTimeLeft(Math.ceil(timeUntilExpiration / 1000 / 60)); // Convert to minutes
          setShowWarning(true);
        }
      } else {
        setShowWarning(false);
      }
    };

    // Check immediately
    checkExpiration();

    // Check every 30 seconds
    const interval = setInterval(checkExpiration, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleRefresh = async () => {
    const refreshTokenValue = TokenStorage.getRefreshToken();
    if (refreshTokenValue) {
      try {
        await refreshToken(refreshTokenValue);
        setShowWarning(false);
        onRefresh?.();
      } catch (error) {
        console.error('Failed to refresh token:', error);
      }
    }
  };

  const handleDismiss = () => {
    setShowWarning(false);
  };

  if (!showWarning) {
    return null;
  }

  return (
    <Alert
      message="Session Expiring Soon"
      description={
        <Space direction="vertical" size="small">
          <span>Your session will expire in {timeLeft} minute{timeLeft !== 1 ? 's' : ''}. Please refresh your session to continue.</span>
          <Space>
            <Button 
              type="primary" 
              size="small" 
              icon={<ReloadOutlined />}
              onClick={handleRefresh}
            >
              Refresh Session
            </Button>
            <Button size="small" onClick={handleDismiss}>
              Dismiss
            </Button>
          </Space>
        </Space>
      }
      type="warning"
      showIcon
      icon={<ExclamationCircleOutlined />}
      style={{
        position: 'fixed',
        top: 16,
        right: 16,
        zIndex: 1000,
        maxWidth: 400,
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
      }}
      closable
      onClose={handleDismiss}
    />
  );
}; 